import { createContext, useContext, useState, useEffect } from 'react';
import { fetchCurrentUser, logout as logoutApi } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Perform an auth check on mount. Tests mock `fetchCurrentUser` so
    // AuthProvider will reflect the mocked auth state during unit tests.
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const userData = await fetchCurrentUser();
      setUser(userData);
    } catch (err) {
      // Not authenticated
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    try {
      await logoutApi();
    } catch (err) {
      console.error('Logout error:', err);
    }
    // Clear local impersonation flag if present and redirect appropriately.
    try {
      const impersonatedBy = localStorage.getItem('impersonatedBy');
      if (impersonatedBy) {
        try { localStorage.removeItem('impersonatedBy'); } catch (e) {}
        try { localStorage.removeItem('impersonation_token'); } catch (e) {}
        setUser(null);
        try { window.location.href = '/admin/users'; } catch (e) {}
        return;
      }
    } catch (e) {
      // ignore storage errors and continue with normal logout flow
    }

    setUser(null);
  }

  function loginWithTwitch() {
    // In development, redirect directly to the API URL (useful for local runs).
    // In production, use a relative path so the request hits the same host
    // (clipto.tv) and benefits from nginx proxying /auth/ to the API.
    // Prefer an explicit API base when provided (VITE_API_URL). If not set,
    // use a relative path so the dev server proxy forwards /auth to the backend.
    const apiUrl = import.meta.env.VITE_API_URL;
    if (apiUrl && apiUrl.length > 0) {
      window.location.href = `${apiUrl.replace(/\/+$/, '')}/auth/twitch`;
    } else {
      window.location.href = '/auth/twitch';
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, logout, loginWithTwitch, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
